import { createElement as _createElement } from "react";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import MuiButton, { buttonClasses } from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';
import Modal from '@mui/material/Modal';
import Slide from '@mui/material/Slide';
import CircularProgress from '@mui/material/CircularProgress';
import { extractDataProps, useDataProps } from '@famly/mf_ts-utils_data-aria-props';
import { Popper } from '@famly/mf_data-display_popper';
import { useMediaQuery, useResponsiveSize } from '@famly/mf_util_breakpoints';
import { Text } from '@famly/mf_data-display_text';
import { useTranslation } from '@famly/mf_util_translation';
import { useModernFamlyContext } from '@famly/mf_system_provider';
import { ButtonBase } from '@famly/mf_input_button';
import { Icon } from '@famly/mf_data-display_icon';
import { adjustAlpha } from '@famly/mf_colors';
import { createStack } from '@famly/mf_layout_stack';
import { DropdownButtonTestAttributes as TestAttributes } from './dropdown-button-test-attributes';
const LoadingOverlayContainer = styled(createStack({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
})) `
    background-color: transparent;
`;
const LoadingOverlay = ({ isLoading }) => {
    return _jsx(LoadingOverlayContainer, { children: isLoading ? _jsx(CircularProgress, { size: 24 }) : null });
};
// The React Compiler cannot lower a template-literal computed object key, so this
// dynamic MUI-class selector is hoisted to module scope and used as a plain
// identifier key (which compiles).
const endIconSelector = `.${buttonClasses.endIcon}`;
export const DropdownButton = (props) => {
    const { options, dropdownPlacement = 'start', ...buttonProps } = props;
    const [isOpen, setIsOpen] = React.useState(false);
    // `ButtonBase` resolves the same default, but the chevron padding below needs the resolved value too.
    const size = useResponsiveSize(buttonProps.size);
    // allow targeting specific dropdown options using intercom.
    // for that we need to update intercom whenever the dropdown gets opened
    React.useEffect(() => {
        if (!isOpen) {
            return;
        }
        if (!window.Intercom) {
            return;
        }
        if (!options.some(buttonProp => buttonProp['data-intercom-target'])) {
            return;
        }
        window.Intercom('update', {
            nonce: new Date(),
        });
    });
    // From an effect, so a discarded or double render cannot report twice; logged rather
    // than thrown so misuse cannot white-screen the page. Loading may just be unresolved.
    const shouldReportEmptyOptions = options.length === 0 && !buttonProps.isLoading;
    React.useEffect(() => {
        if (process.env.NODE_ENV !== 'production' && shouldReportEmptyOptions) {
            console.error('DropdownButton: rendered with an empty `options` list, so it opens an empty menu (on mobile, an action sheet holding only its own heading and Close). Hide the button at the call site when every option has been filtered out.');
        }
    }, [shouldReportEmptyOptions]);
    const anchorEl = React.useRef(null);
    const dropdownRef = React.useRef(null);
    const onOptionClick = React.useCallback((e, option) => {
        setIsOpen(false);
        if (option.onClick) {
            option.onClick(e);
        }
    }, []);
    const useActionSheet = useShouldBehaveAsMobileApp();
    // Close the dropdown on any outside pointer down. We listen in the capture
    // phase so we still catch the event when the dropdown lives inside a
    // parent (e.g. the legacy PortalModal) that stops mouse-event propagation
    // in the bubble phase — MUI's ClickAwayListener listens on `document` in
    // the bubble phase and would never fire in that case.
    React.useEffect(() => {
        if (!isOpen || useActionSheet) {
            return;
        }
        const handlePointerDown = (event) => {
            var _a, _b;
            const target = event.target;
            if (!target) {
                return;
            }
            if (((_a = anchorEl.current) === null || _a === void 0 ? void 0 : _a.contains(target)) || ((_b = dropdownRef.current) === null || _b === void 0 ? void 0 : _b.contains(target))) {
                return;
            }
            setIsOpen(false);
        };
        document.addEventListener('pointerdown', handlePointerDown, true);
        return () => document.removeEventListener('pointerdown', handlePointerDown, true);
    }, [isOpen, useActionSheet]);
    const buttonOptions = React.useMemo(() => options.map((option, index) => (_createElement(DropdownButtonOptionRenderer, { ...extractDataProps(option), key: `option-${index}`, option: option, onOptionClick: onOptionClick, useActionSheet: useActionSheet }))), [onOptionClick, options, useActionSheet]);
    return (_jsxs(_Fragment, { children: [_jsx(ButtonBase, { ...buttonProps, onClick: event => {
                    var _a;
                    (_a = props.onClick) === null || _a === void 0 ? void 0 : _a.call(props, event);
                    setIsOpen(isOpen => !isOpen);
                }, ref: anchorEl, endIcon: _jsx(Icon, { name: "arrow_drop_down", size: 24 }), 
                // With the chevron displayed We need to offset the padding a
                // little so the weight of the button feels right
                sx: theme => ({
                    pr: theme.spacing(size === 'compact' ? 2.5 : 3.5),
                    // Fine-tune spacing between button text and arrow
                    [endIconSelector]: {
                        marginLeft: theme.modernFamlyTheme.spacing(0),
                    },
                }) }), useActionSheet ? (_jsx(MobileActionSheet, { headerItemText: props.text, isOpen: isOpen, onClickAway: () => setIsOpen(false), disablePortal: props.disablePortal, children: buttonOptions })) : (_jsx(_Fragment, { children: anchorEl.current ? (_jsx(DesktopDropdown, { anchorEl: anchorEl.current, dropdownRef: dropdownRef, isOpen: isOpen, onClickAway: () => setIsOpen(false), placement: dropdownPlacement, disablePortal: props.disablePortal, children: buttonOptions })) : null }))] }));
};
const DesktopDropdown = (props) => {
    const placement = (() => {
        switch (props.placement) {
            case 'end':
                return 'bottom-end';
            case 'top-start':
                return 'top-start';
            default:
                return 'bottom-start';
        }
    })();
    // We want the dropdown to take up at least the same amount of
    // space as the button, so we observe the dimensions of the anchor
    // to to detect any size changes.
    const anchorElDimension = useElementDimension(props.anchorEl);
    return (_jsx(Popper, { placement: placement, open: props.isOpen, anchorEl: props.anchorEl, transition: true, disablePortal: props.disablePortal, "data-e2e-class": TestAttributes.popup, children: ({ TransitionProps }) => (_jsx(ClickAwayListener, { onClickAway: event => {
                // The button toggles the menu itself, so a click on it is never a click-away.
                // This listener outlives the fade-out and would close a menu the button just reopened.
                if (props.anchorEl.contains(event.target)) {
                    return;
                }
                props.onClickAway();
            }, children: _jsx(Fade, { ...TransitionProps, timeout: 200, children: _jsx(DropdownContainer, { ref: props.dropdownRef, minWidth: anchorElDimension.width, divider: _jsx(Divider, {}), role: "listbox", children: props.children }) }) })) }));
};
const MobileActionSheet = (props) => {
    const closeButtonText = useTranslation('DropdownButton.close');
    const { onClickAway } = props;
    // Ensure clicks on the backdrop don't propagate to elements behind the modal
    // This prevents issues where clicking the backdrop would e.g. close both the dropdown
    // but also trigger a click on an element behind it (e.g. a dropdown button is
    // rendered inside a clickable element).
    const handleBackdropClick = React.useCallback((event) => {
        event.stopPropagation();
    }, []);
    // Ensure clicks on the backdrop don't propagate to elements behind the modal
    // This prevents issues where clicking the backdrop would e.g. close both the dropdown
    // but also trigger a click on an element behind it (e.g. a dropdown button is
    // rendered inside a clickable element).
    const handleCancelClick = React.useCallback((event) => {
        event.stopPropagation();
        onClickAway();
    }, [onClickAway]);
    return (_jsx(Modal, { open: props.isOpen, disablePortal: props.disablePortal, slotProps: {
            backdrop: {
                onClick: handleBackdropClick,
            },
        }, children: _jsx(Slide, { direction: "up", in: props.isOpen, children: _jsx(ModalPosition, { "data-e2e-class": TestAttributes.popup, children: _jsx(ClickAwayListener, { onClickAway: onClickAway, children: _jsxs(Stack, { gap: 2, children: [_jsxs(DropdownContainer, { divider: _jsx(Divider, {}), role: "dialog", children: [props.headerItemText ? (_jsx(Text, { variant: "body-small", emphasized: true, color: "n300", textAlign: "center", height: "48px", display: "flex", justifyContent: "center", alignItems: "center", children: props.headerItemText })) : null, props.children] }), _jsx(MuiButton, { variant: "secondary", onClick: handleCancelClick, children: closeButtonText })] }) }) }) }) }));
};
const ModalPosition = styled('div') `
    position: absolute;
    bottom: calc(env(safe-area-inset-bottom) + 8px);
    left: 8px;
    right: 8px;
`;
const DropdownButtonOptionRenderer = ({ option, onOptionClick, useActionSheet, }) => {
    const { label, onClick, color, icon, description, isSelected, ...rest } = option;
    const iconName = typeof icon === 'string' ? icon : icon === null || icon === void 0 ? void 0 : icon.name;
    const iconColor = typeof icon === 'string' ? color : icon === null || icon === void 0 ? void 0 : icon.color;
    const anchorProps = rest.href
        ? {
            href: rest.href,
            as: 'a',
            target: rest.target || undefined,
        }
        : {};
    const dataProps = useDataProps(rest);
    return (_jsxs(DropdownOptionButton, { role: rest.href ? 'link' : 'option', color: color, onClick: e => {
            e.stopPropagation();
            onOptionClick(e, option);
        }, 
        // needs to be of type button or might otherwise submit modals with forms
        // https://famlyapp.slack.com/archives/C1PLD0U49/p1689603674665169
        type: "button", disabled: rest.disabled, "aria-selected": rest.href ? undefined : isSelected, ...anchorProps, ...dataProps, children: [_jsxs(Stack, { direction: "row", gap: 1, alignItems: "center", justifyContent: useActionSheet ? 'center' : 'flex-start', flexGrow: "1", children: [iconName && !useActionSheet ? _jsx(Icon, { size: 18, name: iconName, color: iconColor }) : null, _jsxs(Stack, { direction: "column", textAlign: "start", children: [_jsx(Text, { variant: useActionSheet ? 'body' : 'body-small', children: label }), description && !useActionSheet && (_jsx(Text, { "data-e2e-class": "dropdown-option-description", color: "n300", variant: "micro", maxWidth: "300px", lines: 2, children: description }))] })] }), isSelected && !rest.href ? (_jsx(Icon, { name: "check", size: 18, color: "p400", "data-e2e-class": TestAttributes.selectedMark })) : null, _jsx(LoadingOverlay, { isLoading: rest.isLoading })] }));
};
const DropdownContainer = styled(Stack) `
    min-width: ${props => { var _a; return (_a = props.minWidth) !== null && _a !== void 0 ? _a : 0; }}px;
    margin: ${props => props.theme.spacing(0.5, 0)};
    border: 1px solid ${props => props.theme.modernFamlyTheme.colorPalette.n75};
    border-radius: 8px;
    background-color: ${props => props.theme.modernFamlyTheme.colorPalette.n0};
    box-shadow: ${props => props.theme.modernFamlyTheme.elevation[2]};
    overflow: hidden;
`;
const DropdownOptionButton = styled('button') `
    color: ${props => { var _a; return props.theme.modernFamlyTheme.colorPalette[(_a = props.color) !== null && _a !== void 0 ? _a : 'n400']; }};
    font-size: ${props => props.theme.typography['body-small'].fontSize};
    font-family: ${props => props.theme.typography.fontFamily};
    padding: 0;
    margin: 0;
    border: none;
    background: none;
    display: inline-flex;
    min-height: 48px;
    align-items: center;
    gap: ${props => props.theme.spacing(2)};
    padding-left: ${props => props.theme.spacing(4)};
    padding-right: ${props => props.theme.spacing(4)};
    padding-top: ${props => props.theme.spacing(2)};
    padding-bottom: ${props => props.theme.spacing(2)};
    box-sizing: border-box;
    cursor: pointer;
    position: relative;

    &:hover {
        background-color: ${props => props.theme.modernFamlyTheme.colorPalette.p50};
    }
    // This is needed in case href is passed in as a prop.
    // When that happens, the button acts as an anchor tag,
    // so we remove the underlining.
    text-decoration: none;

    &:disabled {
        background-color: ${props => props.theme.modernFamlyTheme.colorPalette.n75};
        color: ${props => adjustAlpha(0.6, props.theme.modernFamlyTheme.colorPalette.n300)};
    }
`;
/**
 * Utility hook to observe the dimensions of the passed element. Whenever
 * the element changes size, the returned dimensions will update.
 */
const useElementDimension = (element) => {
    const [dimensions, setDimensions] = React.useState({ width: 0, height: 0 });
    const resizeObserver = React.useMemo(() => new ResizeObserver(entries => {
        for (const entry of entries) {
            const clientRect = entry.target.getBoundingClientRect();
            setDimensions({ width: clientRect.width, height: clientRect.height });
        }
    }), []);
    // We use a layout effect here so we're sure that the
    // observer runs before the browser paints the screen
    React.useLayoutEffect(() => {
        resizeObserver.observe(element);
        return () => resizeObserver.unobserve(element);
    }, [element, resizeObserver]);
    return dimensions;
};
const useShouldBehaveAsMobileApp = () => {
    // Instead of using the nasty "customAtLeastOneLargerThanLandscape" breakpoint we hardcode the 1025px constant here.
    // This is in line with what the `useActionSheet` hook returns in app/react/components/action-sheet/action-sheet.tsx
    const shouldBehaveAsMobileApp = useMediaQuery(breakpoints => breakpoints.down(1025));
    const { isMobileApp } = useModernFamlyContext();
    return isMobileApp || shouldBehaveAsMobileApp;
};
